import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/src/types';
import { Card, CardTypeEnum } from '@varo/graphql-schema-types';
import believeCardA from 'assets/images/vxl/cards/believe-card-a.png';
import debitCardA from 'assets/images/vxl/cards/debit-card-a.png';
import { usePathname } from 'expo-router';
import { bottomSheetScrollConfig } from 'features/billSwitch/helpers/bottomSheetScrollConfig';
import { logErrorEvent } from 'features/billSwitch/helpers/logErrorEvent';
import React, { forwardRef, RefObject, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useFreezeCard } from 'shared/hooks/useFreezeCard/useFreezeCard';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface UnfreezeCardsBottomSheetProps {
  useBelieveLanguage?: boolean;
  cards?: Card[];
  hasCards?: boolean;
  hasLockedCards?: boolean;
  analyticsMetadata?: { source_name: string };
  onSuccess: () => void;
  onDismiss: () => void;
}

const UnfreezeCardsBottomSheet = (
  {
    useBelieveLanguage,
    hasLockedCards,
    hasCards,
    cards,
    analyticsMetadata: { source_name } = { source_name: 'unknown' },
    onSuccess,
    onDismiss,
  }: UnfreezeCardsBottomSheetProps,
  bottomSheetRef: RefObject<BottomSheetModalMethods>,
) => {
  const { t: translatedText } = useTranslation('billSwitch', {
    keyPrefix: 'unfreezeCardsBottomSheet',
  });

  const [lockingId, setLockingId] = React.useState<string | null>(null);
  const { setSnackBarMessage } = useContext(GlobalUIContext);
  const { setFrozenState, fetching, error: freezeError } = useFreezeCard();
  const { segmentClient } = useAnalyticsConfiguration();
  const pathname = usePathname();

  useEffect(() => {
    if (freezeError) {
      setSnackBarMessage?.(freezeError.message);
      logErrorEvent({
        segmentClient,
        error_message:
          '[Card Unlocking Error]: ' +
          (freezeError.message ?? 'Error unlocking card'),
        source_name,
      });
    }
  }, [segmentClient, source_name, freezeError, setSnackBarMessage]);

  const toggleCardLock = async (cardId: string, toFrozen: boolean) => {
    setLockingId(cardId);
    await setFrozenState(cardId, toFrozen);
    setLockingId(null);
  };

  const { title, body } = (() => {
    if (hasCards) {
      return {
        title: translatedText('title'),
        body: translatedText('body'),
      };
    }
    if (useBelieveLanguage) {
      return {
        title: translatedText('error.noActiveCardsBelieve.title'),
        body: translatedText('error.noActiveCardsBelieve.message'),
      };
    }
    return {
      title: translatedText('error.noActiveCards.title'),
      body: translatedText('error.noActiveCards.message'),
    };
  })();

  return (
    <BottomSheet
      ref={bottomSheetRef}
      allowScrolling={bottomSheetScrollConfig()}
      analyticsId={[pathname, 'cardFrozenBottomSheet'].join('/')}
    >
      <View style={styles.container} testID="unfreeze-cards-bottom-sheet">
        <View style={styles.textContainer}>
          <TitleMdText>{title}</TitleMdText>
          <BodyBaseText>{body}</BodyBaseText>
        </View>
        {hasCards ? (
          <ListItemGroup
            items={
              cards?.map((card: Card) => ({
                keyId: card.id,
                leadingAccessory: {
                  id: `${card.id}-leading-accessory`,
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    imageSource:
                      card.type === CardTypeEnum.ChargeCard
                        ? (believeCardA as ImageSourcePropType)
                        : (debitCardA as ImageSourcePropType),
                    imageStyle: {
                      height: 36,
                      width: 36,
                    },
                    resizeMode: 'contain',
                    variant: 'MD',
                  },
                },
                lineFormat: ListItemLineFormat.LABEL,
                primaryCopy:
                  card.type === CardTypeEnum.ChargeCard
                    ? translatedText('cardItem.title.believe')
                    : translatedText('cardItem.title.debit'),
                secondaryCopy: card.isLocked
                  ? translatedText('cardItem.state.frozen')
                  : translatedText('cardItem.state.unfrozen'),
                trailingAccessory: {
                  id: `${card.id}-trailing-accessory`,
                  name: VXLComponentId.SWITCH,
                  props: {
                    isOn: card.isLocked,
                    onValueChange: () => {
                      void toggleCardLock(card.id, !card.isLocked);
                    },
                    isDisabled:
                      !card.isLocked || (lockingId !== card.id && fetching),
                    isLoading: lockingId === card.id && fetching,
                    analyticsId: 'bottomSheetSwitch',
                    customAttributes: {
                      card_type: card.type,
                    },
                  },
                },
                showsChevron: false,
              })) ?? []
            }
          />
        ) : null}
        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('buttons.primary')}
            type="PRIMARY"
            isDisabled={hasLockedCards || !hasCards}
            onPress={onSuccess}
            analyticsId="bottomSheetPrimary"
          />
          <Button
            label={translatedText('buttons.secondary')}
            type="SECONDARY"
            onPress={onDismiss}
            analyticsId="bottomSheetSecondary"
          />
        </View>
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    gap: theme.spacing.m,
  },
  container: {
    gap: theme.spacing.l,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  textContainer: {
    gap: theme.spacing.s,
  },
});

export default forwardRef(UnfreezeCardsBottomSheet);
